import * as React from 'react';
import styles from './index.less';
import FComponentsLib from '@freelog/components-lib';
import FCoverImage from '@/components/FCoverImage';
import { FI18n, FUtil } from '@freelog/tools-lib';
import FTooltip from '@/components/FTooltip';
import FResourceCard from '@/components/FResourceCard';
import { Popover } from 'antd';
import img_choiceLabel from '@/assets/choice-label@2x.png';
import FResourceStatusBadge from '@/components/FResourceStatusBadge';
import * as AHooks from 'ahooks';
import FCheckbox from '@/components/FCheckbox';
import FIcons from '@/components/FIcons';
import { ReactSortable } from 'react-sortablejs';

interface CollectionListProps {
  resources: {
    itemID: string;
    itemTitle: string;
    itemSerial: number;
    itemNumber: string;
    resourceInfo: {
      id: string;
      cover: string;
      name: string;
      title: string;
      version: string;
      policy: string[];
      type: string[];
      status: 0 | 1 | 2 | 4;
      authProblem: boolean;
      updateDate: string;
      username: string;
      useAvatar: string;
      isChoice: boolean;
      intro: string;
      releaseDateTime?: string;
    };
  }[];

  showCheckbox: 'show' | 'hide';
  checkedItemIDs: string[];
  showTitle: 'title' | 'serial' | 'custom' | 'customNotEdited' | 'hide';
  showNumber: 'show' | 'hide';
  showCover: 'show' | 'hide';
  showIntroduction: 'show' | 'hide';

  draggable?: boolean;

  onChangeChecked?(itemsID: string, checked: boolean): void;

  onChangeCustomTitle?(itemID: string, title: string): void;

  onClickDelete?(itemID: string): void;

  onClick?(itemID: string, resourceID: string): void;

  onChangeIndex?(itemID: string, index: number, newList: CollectionListProps['resources']): void;
}

interface CollectionListStates {
  resource: {
    id: string;
    itemID: string;
    itemTitle: string;
    itemSerial: number;
    itemNumber: string;
    resourceInfo: {
      id: string;
      cover: string;
      name: string;
      title: string;
      version: string;
      policy: string[];
      type: string[];
      status: 0 | 1 | 2 | 4;
      authProblem: boolean;
      updateDate: string;
      username: string;
      useAvatar: string;
      isChoice: boolean;
      intro: string;
      releaseDateTime?: string;
    };
  }[];
}

function CollectionList({
  resources,
  onChangeCustomTitle,
  onClickDelete,
  onClick,
  showCheckbox,
  checkedItemIDs,
  draggable,
  onChangeChecked,
  showTitle,
  showNumber,
  showCover,
  showIntroduction,
  onChangeIndex,
}: CollectionListProps) {
  const [$introTooltipOpen, set$introTooltipOpen, get$introTooltipOpen] =
    FUtil.Hook.useGetState<boolean>(false);
  const [$customTitleMouseEnter, set$customTitleMouseEnter, get$customTitleMouseEnter] =
    FUtil.Hook.useGetState<boolean>(false);
  const [$resourceCardOpen, set$resourceCardOpen, get$resourceCardOpen] =
    FUtil.Hook.useGetState<boolean>(false);

  const [$resource, set$resource, get$resource] = FUtil.Hook.useGetState<
    CollectionListStates['resource']
  >(
    resources.map((r) => {
      return {
        id: r.itemID,
        ...r,
      };
    }),
  );

  AHooks.useDebounceEffect(
    () => {
      set$resource(
        resources.map((r) => {
          return {
            id: r.itemID,
            ...r,
          };
        }),
      );
    },
    [resources],
    {
      wait: 100,
    },
  );

  return (
    <ReactSortable
      className={styles.list}
      style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
      }}
      list={$resource}
      setList={(newState, sortable, store) => {
        // console.log(newState, sortable, store, '89sdlkfjl;kajsdlkfklsdjlkj');
        set$resource(newState);
      }}
      onEnd={(evt) => {
        // console.log(evt, 'isodjflksdklflsjdfjls;dfj');
        const resource: CollectionListStates['resource'] = get$resource();
        const index: number = evt.newDraggableIndex ?? -1;
        const id: string = resource[index]?.id || '';
        const newList = resource.map(({ id, ...r }) => {
          return r;
        });
        // console.log(id, index, newList, '89ewijf;klsdjflkjdskljfk;lsjdklfjlksdjflksdjlkj');
        onChangeIndex?.(id, index, newList);
      }}
      handle={'.' + styles.dragHandle}
      forceAutoScrollFallback={true}
      // chosenClass={''}
      // dragClass={''}
      disabled={!draggable}
    >
      {$resource.map((i, j) => {
        return (
          <div key={i.itemID} className={styles.item}>
            {showCheckbox !== 'hide' && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FCheckbox
                  checked={checkedItemIDs.includes(i.itemID)}
                  onChange={(e) => {
                    onChangeChecked?.(i.itemID, e.target.checked);
                  }}
                />
              </div>
            )}

            {
              // setting.collection_item_no_display === 'collection_item_no_display_show'
              showNumber === 'show' && <div className={styles.serial}>{i.itemSerial || -1}</div>
            }

            <a
              className={styles.body}
              onClick={() => {
                onClick?.(i.itemID, i.resourceInfo.id);
              }}
              style={{ cursor: onClick ? 'pointer' : 'auto' }}
              title={
                !onClick || $introTooltipOpen || $customTitleMouseEnter || $resourceCardOpen
                  ? undefined
                  : FI18n.i18nNext.t('cqr_itemmgmt_entry_tooltips')
              }
            >
              {
                // setting.collection_item_image_display === 'collection_item_image_display_show'
                showCover === 'show' && (
                  <>
                    <div
                      style={{
                        width: 'fit-content',
                        height: 'fit-content',
                        position: 'relative',
                        opacity: i.resourceInfo.status === 2 ? 0.4 : 1,
                      }}
                    >
                      <Cover
                        cover={i.resourceInfo.cover}
                        width={
                          showTitle !== 'hide' && showIntroduction !== 'hide'
                            ? 88
                            : showTitle === 'hide' && showIntroduction === 'hide'
                            ? 44
                            : 60
                        }
                      />

                      {i.resourceInfo.isChoice && (
                        <FTooltip title={'编辑精选'} placement={'bottomLeft'}>
                          <img
                            src={img_choiceLabel}
                            alt={''}
                            style={{
                              width: 16,
                              height: 16,
                              position: 'absolute',
                              top: 4,
                              left: 4,
                            }}
                          />
                        </FTooltip>
                      )}
                    </div>

                    <div style={{ flexBasis: 10, flexShrink: 0 }} />
                  </>
                )
              }

              <div
                style={{
                  flexBasis: '100%',
                  flexShrink: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                  overflow: 'hidden',
                  opacity: i.resourceInfo.status === 2 ? 0.4 : 1,
                }}
              >
                {showTitle === 'title' && (
                  <OverflowTooltip
                    text={i.resourceInfo.title || i.resourceInfo.name.split('/')[1]}
                    className={styles.title}
                  />
                )}

                {showTitle === 'serial' && (
                  <OverflowTooltip
                    text={i.itemNumber || '(无编号)'}
                    className={styles.title}
                    style={{ opacity: i.itemNumber ? 1 : 0.2 }}
                  />
                )}

                {showTitle === 'custom' && (
                  <>
                    {!!onChangeCustomTitle && (
                      <CustomTitleInput
                        value={i.itemTitle}
                        onChange={(v) => {
                          // console.log('******************* sdiofjlsdkjflsjdklfjolkj');
                          onChangeCustomTitle(i.itemID, v);
                        }}
                        onMouseEnter={(enter) => {
                          set$customTitleMouseEnter(enter);
                        }}
                      />
                    )}

                    {!onChangeCustomTitle && (
                      <OverflowTooltip
                        text={i.itemTitle}
                        className={styles.title}
                        afterOpenChange={(o) => {
                          // console.log(o, '***************************************');
                          set$introTooltipOpen(o);
                        }}
                      />
                    )}
                  </>
                )}

                {showTitle === 'customNotEdited' && (
                  <OverflowTooltip
                    text={i.itemTitle || i.resourceInfo.name.split('/')[1]}
                    className={styles.title}
                  />
                )}

                {
                  // setting.collection_item_descr_display === 'collection_item_descr_display_show'
                  showIntroduction === 'show' && (
                    <OverflowTooltip
                      text={i.resourceInfo.intro || '(暂无简介)'}
                      className={styles.intro}
                      style={{ opacity: i.resourceInfo.intro ? 1 : 0.6 }}
                      afterOpenChange={(o) => {
                        // console.log(o, '***************************************');
                        set$introTooltipOpen(o);
                      }}
                    />
                  )
                }

                <BaseInfo
                  resourceInfo={i.resourceInfo}
                  onOpenChange={(o) => {
                    set$resourceCardOpen(o);
                  }}
                />
              </div>

              {i.resourceInfo.status !== 2 && i.resourceInfo.authProblem && (
                <div
                  style={{
                    flexBasis: 100,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FTooltip
                    // title={'存在授权问题'}
                    title={FI18n.i18nNext.t('alert_resource_no_auth')}
                  >
                    <FIcons.FWarning style={{ fontSize: 16 }} />
                  </FTooltip>
                </div>
              )}

              {i.resourceInfo.status === 2 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexBasis: 50,
                    flexShrink: 0,
                    justifyContent: 'flex-end',
                  }}
                >
                  <FResourceStatusBadge status={'freeze'} />
                </div>
              )}

              {draggable && (
                <a className={styles.dragHandle} title={'拖动排序'}>
                  <FIcons.FDrag style={{ color: '#666' }} />
                </a>
              )}
            </a>
            {onClickDelete && (
              <div
                className={styles.delete}
                onClick={() => {
                  onClickDelete(i.itemID);
                }}
              >
                <FIcons.FDelete />
              </div>
            )}
          </div>
        );
      })}
    </ReactSortable>
  );

  // return (<FDndContainer>
  //   <div className={styles.list}>
  //     {
  //       $resource
  //         .map((i, j) => {
  //           return (<FDndItem
  //             id={i.itemID}
  //             index={j}
  //             key={i.itemID}
  //             className={styles.item}
  //             onMove={(dragIndex: number, hoverIndex: number) => {
  //               const data = getNewList<CollectionListProps['resources'][number]>(get$resource(), dragIndex, hoverIndex);
  //               set$resource(data);
  //             }}
  //             onEnd={(id: any, index: number) => {
  //               onChangeIndex?.(id, index, get$resource());
  //             }}
  //           >
  //             {
  //               showCheckbox !== 'hide' && (<div style={{ display: 'flex', alignItems: 'center' }}>
  //                 <FCheckbox
  //                   checked={checkedItemIDs.includes(i.itemID)}
  //                   onChange={(e) => {
  //                     onChangeChecked?.(i.itemID, e.target.checked);
  //                   }}
  //                 />
  //               </div>)
  //             }
  //
  //             {
  //               // setting.collection_item_no_display === 'collection_item_no_display_show'
  //               showNumber === 'show'
  //               && (
  //                 <div className={styles.serial}>{i.itemSerial || -1}</div>)
  //             }
  //
  //             <a
  //               className={styles.body}
  //               onClick={() => {
  //                 onClick?.(i.itemID, i.resourceInfo.id);
  //               }}
  //               style={{ cursor: onClick ? 'pointer' : 'auto' }}
  //               title={(
  //                 !onClick || $introTooltipOpen || $customTitleMouseEnter || $resourceCardOpen
  //               ) ? undefined : FI18n.i18nNext.t('cqr_itemmgmt_entry_tooltips')}
  //             >
  //               {
  //                 // setting.collection_item_image_display === 'collection_item_image_display_show'
  //                 showCover === 'show'
  //                 && (<>
  //                   <div
  //                     style={{
  //                       width: 'fit-content',
  //                       height: 'fit-content',
  //                       position: 'relative',
  //                       opacity: i.resourceInfo.status === 2 ? .4 : 1,
  //                     }}
  //                   >
  //
  //                     <Cover
  //                       cover={i.resourceInfo.cover}
  //                       // width={setting.collection_item_title !== 'collection_item_title_empty' && setting.collection_item_descr_display !== 'collection_item_descr_display_hide'
  //                       //   ? 88
  //                       //   : setting.collection_item_title === 'collection_item_title_empty' && setting.collection_item_descr_display === 'collection_item_descr_display_hide'
  //                       //     ? 44
  //                       //     : 60}
  //                       width={showTitle !== 'hide' && showIntroduction !== 'hide'
  //                         ? 88
  //                         : showTitle === 'hide' && showIntroduction === 'hide'
  //                           ? 44
  //                           : 60}
  //                     />
  //
  //                     {
  //                       i.resourceInfo.isChoice && (<FTooltip title={'编辑精选'} placement={'bottomLeft'}>
  //                         <img
  //                           src={img_choiceLabel}
  //                           alt={''}
  //                           style={{ width: 16, height: 16, position: 'absolute', top: 4, left: 4 }}
  //                         />
  //                       </FTooltip>)
  //                     }
  //                   </div>
  //
  //                   <div style={{ flexBasis: 10, flexShrink: 0 }} />
  //                 </>)
  //               }
  //
  //               <div
  //                 style={{
  //                   flexBasis: '100%',
  //                   flexShrink: 1,
  //                   display: 'flex',
  //                   flexDirection: 'column',
  //                   gap: 5,
  //                   overflow: 'hidden',
  //                   opacity: i.resourceInfo.status === 2 ? .4 : 1,
  //                 }}
  //               >
  //                 {
  //                   // setting.collection_item_title === 'collection_item_title_rtitle'
  //                   showTitle === 'title'
  //                   && (<OverflowTooltip
  //                     text={i.resourceInfo.title || i.resourceInfo.name.split('/')[1]}
  //                     className={styles.title}
  //                   />)
  //                 }
  //
  //                 {
  //                   // setting.collection_item_title === 'collection_item_title_sn'
  //                   showTitle === 'serial'
  //                   && (<OverflowTooltip
  //                     text={i.itemNumber || '(无编号)'}
  //                     className={styles.title}
  //                     style={{ opacity: i.itemNumber ? 1 : .2 }}
  //                   />)
  //                 }
  //
  //                 {
  //                   // setting.collection_item_title === 'collection_item_title_custom'
  //                   showTitle === 'custom'
  //                   && (<>
  //                     {
  //                       !!onChangeCustomTitle && (
  //                         <CustomTitleInput
  //                           value={i.itemTitle}
  //                           onChange={(v) => {
  //                             // console.log('******************* sdiofjlsdkjflsjdklfjolkj');
  //                             onChangeCustomTitle(i.itemID, v);
  //                           }}
  //                           onMouseEnter={(enter) => {
  //                             set$customTitleMouseEnter(enter);
  //                           }}
  //                         />)
  //                     }
  //
  //                     {
  //                       !onChangeCustomTitle && (<OverflowTooltip
  //                         text={i.itemTitle}
  //                         className={styles.title}
  //                         afterOpenChange={(o) => {
  //                           // console.log(o, '***************************************');
  //                           set$introTooltipOpen(o);
  //                         }}
  //                       />)
  //                     }
  //                   </>)
  //                 }
  //
  //                 {
  //                   // setting.collection_item_descr_display === 'collection_item_descr_display_show'
  //                   showIntroduction === 'show'
  //                   && (<OverflowTooltip
  //                     text={i.resourceInfo.intro || '(暂无简介)'}
  //                     className={styles.intro}
  //                     style={{ opacity: i.resourceInfo.intro ? 1 : .6 }}
  //                     afterOpenChange={(o) => {
  //                       // console.log(o, '***************************************');
  //                       set$introTooltipOpen(o);
  //                     }}
  //                   />)
  //                 }
  //
  //                 <BaseInfo
  //                   resourceInfo={i.resourceInfo}
  //                   onOpenChange={(o) => {
  //                     set$resourceCardOpen(o);
  //                   }}
  //                 />
  //               </div>
  //
  //               <div style={{
  //                 flexBasis: 100,
  //                 flexShrink: 0,
  //                 display: 'flex',
  //                 alignItems: 'center',
  //                 justifyContent: 'center',
  //               }}>
  //                 {
  //                   i.resourceInfo.authProblem &&
  //                   <FTooltip title={'存在授权问题'}><FIcons.FWarning style={{ fontSize: 16 }} /></FTooltip>
  //                 }
  //               </div>
  //
  //               {
  //                 i.resourceInfo.status === 2 && (
  //                   <div style={{
  //                     display: 'flex',
  //                     alignItems: 'center',
  //                     flexBasis: 50,
  //                     flexShrink: 0,
  //                     justifyContent: 'flex-end',
  //                   }}>
  //                     <FResourceStatusBadge
  //                       status={'freeze'}
  //                     />
  //                   </div>)
  //               }
  //
  //             </a>
  //             {
  //               onClickDelete && (<div
  //                 className={styles.delete}
  //                 onClick={() => {
  //                   onClickDelete(i.itemID);
  //                 }}
  //               >
  //                 <FIcons.FDelete />
  //               </div>)
  //             }
  //
  //           </FDndItem>);
  //         })
  //     }
  //   </div>
  // </FDndContainer>);
}

export default CollectionList;

interface BaseInfoProps {
  resourceInfo: {
    id: string;
    cover: string;
    name: string;
    title: string;
    version: string;
    policy: string[];
    type: string[];
    status: 0 | 1 | 2 | 4;
    authProblem: boolean;
    updateDate: string;
    username: string;
    useAvatar: string;
    isChoice: boolean;
    intro: string;
    releaseDateTime?: string;
  };

  onOpenChange?(o: boolean): void;
}

export function BaseInfo({ resourceInfo, onOpenChange }: BaseInfoProps) {
  const ref = React.useRef<any>(null);

  return (
    <div
      style={{
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
      }}
      ref={ref}
    >
      <FComponentsLib.FContentText
        text={FI18n.i18nNext.t('cqr_itemmgmt_entry_resourceinfo')}
        type={'additional2'}
        style={{ color: '#666', flexBasis: 'fit-content', flexShrink: 0 }}
      />
      <Popover
        content={
          <FResourceCard
            key={resourceInfo.id}
            resource={resourceInfo}
            onClick={(event) => {
              event.stopPropagation();
              window.open(
                FUtil.LinkTo.resourceDetails({
                  resourceID: resourceInfo.id,
                }),
              );
            }}
          />
        }
        title={null}
        placement={'bottom'}
        overlayClassName={styles.resourceCardPopover}
        onOpenChange={(o) => {
          onOpenChange?.(o);
        }}
        getPopupContainer={() => {
          return ref.current;
        }}
      >
        <div
          style={{ width: 'fit-content' }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <FComponentsLib.FTextBtn
            type='default'
            onClick={() => {
              window.open(FUtil.LinkTo.resourceDetails({ resourceID: resourceInfo.id }));
            }}
          >
            <FComponentsLib.FContentText
              text={resourceInfo.title || resourceInfo.name.split('/')[1]}
              type={'additional2'}
              style={{
                color: 'inherit',
                textDecoration: 'underline',
                maxWidth: 200,
              }}
              singleRow
            />
          </FComponentsLib.FTextBtn>
        </div>
      </Popover>
      <FComponentsLib.FContentText
        text={'|'}
        type={'additional2'}
        style={{ color: '#666', flexBasis: 'fit-content', flexShrink: 0 }}
      />
      <FComponentsLib.FContentText
        text={resourceInfo.type.join('/')}
        type={'additional2'}
        style={{ color: '#666', flexBasis: 'fit-content', flexShrink: 0 }}
      />
      <FComponentsLib.FContentText
        text={'|'}
        type={'additional2'}
        style={{ color: '#666', flexBasis: 'fit-content', flexShrink: 0 }}
      />
      <FComponentsLib.FContentText
        // text={`更新时间${resourceInfo.updateDate}`}
        text={FI18n.i18nNext.t('cqr_itemmgmt_entry_lastupdated', {
          Timestamp: resourceInfo.releaseDateTime || resourceInfo.updateDate,
        })}
        type={'additional2'}
        style={{ color: '#666', flexBasis: 'fit-content', flexShrink: 1 }}
        singleRow
      />
    </div>
  );
}

interface CoverProps {
  cover: string;
  width: number;
}

function Cover({ width, cover }: CoverProps) {
  return (
    <div
      style={{
        flexBasis: width,
        height: (width / 4) * 3,
        borderRadius: 4,
        flexShrink: 0,
      }}
    >
      <FCoverImage src={cover} width={width} />
    </div>
  );
}

interface OverflowTooltipProps {
  text: string;
  className?: string;
  style?: React.CSSProperties;

  afterOpenChange?(visible: boolean): void;
}

export function OverflowTooltip({ text, className, style, afterOpenChange }: OverflowTooltipProps) {
  const ref = React.useRef<any>(null);
  const [$visible, set$visible] = FUtil.Hook.useGetState<boolean | undefined>(false);

  return (
    <FTooltip
      title={text}
      open={$visible}
      trigger={[]}
      // afterOpenChange={afterOpenChange}
    >
      <div
        className={className}
        style={style}
        ref={ref}
        onMouseEnter={(e) => {
          if (
            ref.current.clientWidth < ref.current.scrollWidth ||
            ref.current.clientHeight < ref.current.scrollHeight
          ) {
            set$visible(true);
            afterOpenChange?.(true);
          }
        }}
        onMouseLeave={() => {
          set$visible(false);
          afterOpenChange?.(false);
        }}
      >
        {text}
      </div>
    </FTooltip>
  );
}

interface CustomTitleInputProps {
  value: string;

  onChange?(value: string): void;

  onMouseEnter?(enter: boolean): void;
}

export function CustomTitleInput({ value, onChange, onMouseEnter }: CustomTitleInputProps) {
  const [$value, set$value, get$value] = FUtil.Hook.useGetState<string>(value);

  AHooks.useDebounceEffect(
    () => {
      if (value !== get$value()) {
        set$value(value);
      }
    },
    [value],
    {
      wait: 1,
    },
  );

  const { run } = AHooks.useDebounceFn(
    () => {
      onChange && onChange(get$value());
    },
    {
      wait: 300,
    },
  );

  return (
    <div
      style={{ width: '100%' }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onMouseEnter={() => {
        onMouseEnter?.(true);
      }}
      onMouseLeave={() => {
        onMouseEnter?.(false);
      }}
    >
      <FComponentsLib.FInput.FSingleLine
        lengthLimit={100}
        size={'small'}
        value={$value}
        style={{ width: '100%' }}
        onChange={(e) => {
          e.stopPropagation();
          set$value(e.target.value.substring(0, 100));
          run();
        }}
      />
    </div>
  );
}
